/**
 * <product-tabs> (sections/product-tabs.liquid).
 *
 * Which tabs exist, what they are called and what is inside them is entirely
 * the merchant's arrangement of blocks - this file knows only that the
 * section printed some number of [data-tab-item]s, each with a label and a
 * panel. The one exception is the recommendations slot, at the bottom.
 *
 * The page carries ONE copy of the content and both sets of controls: an
 * accordion <button aria-expanded> per panel, printed by the tab block, and a
 * [role=tab] per panel in the tablist, built here from those - see
 * buildTabs(). CSS displays exactly one set - with a script running it is
 * always the tab row, which scrolls sideways on a phone rather than becoming
 * accordions - and `display: none` removes the other from the accessibility
 * tree along with the pixels. The accordion markup stays for the no-JS case,
 * where plain headings over plain content is the only honest fallback.
 *
 * What CSS cannot do is change the PANEL's semantics, and a panel is where the
 * two patterns genuinely differ: a tab panel is role=tabpanel labelled by its
 * tab and focusable; an accordion panel is a role=region labelled by its
 * header button. So the markup ships the panels with no role at all, and this
 * element applies whichever pair is live - on upgrade and again on every
 * breakpoint crossing. That also means the no-JS page carries no tab/accordion
 * ARIA it cannot honour: it is plain headings over plain content.
 *
 * Selection state is kept per mode and translated across a crossing rather
 * than shared, because "one of N selected" and "any subset expanded" are not
 * the same value. It is also held as element references rather than indices,
 * because the set of tabs can grow after load - see adoptRecommendations().
 *
 * Which tab opens is decided here rather than by the server, because the
 * server cannot see it: a tab is a theme block and decides for itself whether
 * it exists at all, so "the first tab with content" is a fact about the
 * rendered DOM. Every panel is therefore printed shut (`data-inactive`, which
 * CSS honours only under `html.js`) and this file opens exactly one. Nothing
 * flashes; with JS off the attribute is ignored and every panel is open.
 *
 * Keyboard: WAI-ARIA tabs - arrows move (wrapping), Home/End jump to the ends,
 * only the selected tab is in the tab order.
 */
(function () {
  if (customElements.get('product-tabs')) return;

  /* Tabs at every width. The element still carries both patterns - the markup
     ships accordion headers for the no-JS case - but with a script running the
     tab row is now shown at all sizes and scrolls sideways when it does not
     fit, matching tabbed-products. This query is what the element consults to
     decide which ARIA pair to apply, so it answers true everywhere rather than
     the code being torn out: the accordion path is still correct and still
     reachable if the breakpoint is ever wanted back. */
  var DESKTOP_QUERY = '(min-width: 0px)';

  class ProductTabs extends HTMLElement {
    connectedCallback() {
      /* Stacked layout: every panel is on the page under its own heading,
         so there is no tab row to build and nothing to hide. */
      if (this.hasAttribute('data-stacked')) return;
      this.tablist = this.querySelector('[data-tablist]');
      this.buildTabs();
      this.collect();
      this.recsSlot = this.querySelector('[data-recs-slot]');
      if (this.items.length === 0 && this.recsSlot === null) return;

      // The merchant's "open this tab first" wins only if that tab survived
      // rendering; otherwise the first tab that did opens. Never a later one,
      // and never a tab still hidden waiting on its fetch - collect() has
      // already dropped both from this.items.
      var preselected = this.items.filter(function (item) {
        return item.hasAttribute('data-preselected');
      })[0];
      this.selectedItem = preselected || this.items[0] || null;
      this.items.forEach(function (item) {
        item.__expanded = false;
      });
      if (this.selectedItem) this.selectedItem.__expanded = true;

      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);

      this.onTabClick = this.onTabClick.bind(this);
      this.onTabKeydown = this.onTabKeydown.bind(this);
      this.onHeaderClick = this.onHeaderClick.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.onHashChange = this.onHashChange.bind(this);

      if (this.tablist) {
        this.tablist.addEventListener('click', this.onTabClick);
        this.tablist.addEventListener('keydown', this.onTabKeydown);
      }
      // Bound on the element, not per button, so headers revealed later are
      // covered without rebinding.
      this.addEventListener('click', this.onHeaderClick);
      if (this.desktopQuery.addEventListener) {
        this.desktopQuery.addEventListener('change', this.onModeChange);
      } else if (this.desktopQuery.addListener) {
        this.desktopQuery.addListener(this.onModeChange);
      }
      window.addEventListener('hashchange', this.onHashChange);

      // A link that lands on #reviews (Judge.me writes them, and so does the
      // page's own review summary) must not land on a hidden panel. The hash
      // is resolved before the first render so the panel is already open when
      // we scroll to it.
      var deepLinked = this.itemFromHash();
      if (deepLinked) this.open(deepLinked);
      this.render();
      if (deepLinked) this.scrollTo(deepLinked);

      this.watchRecommendations();
    }

    disconnectedCallback() {
      if (this.tablist) {
        this.tablist.removeEventListener('click', this.onTabClick);
        this.tablist.removeEventListener('keydown', this.onTabKeydown);
      }
      this.removeEventListener('click', this.onHeaderClick);
      if (this.desktopQuery) {
        if (this.desktopQuery.removeEventListener) {
          this.desktopQuery.removeEventListener('change', this.onModeChange);
        } else if (this.desktopQuery.removeListener) {
          this.desktopQuery.removeListener(this.onModeChange);
        }
      }
      window.removeEventListener('hashchange', this.onHashChange);
      if (this.recsObserver) this.recsObserver.disconnect();
    }

    get isDesktop() {
      return this.desktopQuery.matches;
    }

    /* The tab row is built here rather than printed by the section, because a
       section cannot see inside its theme blocks: `section.blocks` hands it
       entries typed "@theme" with no settings, so the labels are unreachable
       there, and which tabs exist at all is decided by each tab block from
       what its own children painted. Both answers live in the DOM, so the row
       is assembled from it.

       Every source of truth for a button comes from the item it belongs to:
       its label from the accordion header that tab printed for itself, its
       target from that panel's id, its `hidden` from the item's. So a button
       cannot name a tab that is not there, and the row cannot drift out of
       step with the panels no matter which tabs dropped out.

       Wholesale rebuild rather than patching, so the one caller that changes
       the set of tabs later (adoptRecommendations) needs no separate path. */
    buildTabs() {
      if (!this.tablist) return;

      var fragment = document.createDocumentFragment();

      Array.prototype.slice
        .call(this.querySelectorAll('[data-tab-item]'))
        .forEach(function (item) {
          var panel = item.querySelector('[data-panel]');
          var label = item.querySelector('[data-tab-label]');
          if (!panel || !label) return;

          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'product-tabs__tab';
          button.setAttribute('role', 'tab');
          button.id = panel.id + '-tab';
          button.setAttribute('aria-controls', panel.id);
          button.setAttribute('aria-selected', 'false');
          button.setAttribute('tabindex', '-1');
          button.setAttribute('data-tab', '');
          // A tab still waiting on its fetch keeps the item's own hidden, so
          // collect() drops the pair together.
          if (item.hasAttribute('hidden')) button.setAttribute('hidden', '');

          var text = document.createElement('span');
          text.className = 'product-tabs__tab-label';
          text.textContent = label.textContent.trim();
          button.appendChild(text);

          fragment.appendChild(button);
        });

      this.tablist.textContent = '';
      this.tablist.appendChild(fragment);
    }

    /* Live tabs only. A [hidden] item is one whose content does not exist yet
       (a tab holding only recommendations, before its fetch lands) and it must
       be invisible to selection, keyboard navigation and deep-linking alike -
       hence a rebuild rather than a filter at each use site. */
    collect() {
      this.items = Array.prototype.slice
        .call(this.querySelectorAll('[data-tab-item]'))
        .filter(function (item) {
          return !item.hasAttribute('hidden');
        });

      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-tab]')).filter(
        function (tab) {
          return !tab.hasAttribute('hidden');
        }
      );
      this.panels = this.items.map(function (item) {
        return item.querySelector('[data-panel]');
      });
      this.headers = this.items.map(function (item) {
        return item.querySelector('[data-accordion]');
      });
    }

    onTabClick(event) {
      var tab = event.target.closest('[data-tab]');
      if (!tab) return;
      var index = this.tabs.indexOf(tab);
      if (index === -1) return;
      this.open(this.items[index]);
      this.render();
    }

    onTabKeydown(event) {
      var current = this.tabs.indexOf(document.activeElement);
      if (current === -1) return;

      var next;
      switch (event.key) {
        case 'ArrowRight':
          next = (current + 1) % this.tabs.length;
          break;
        case 'ArrowLeft':
          next = (current - 1 + this.tabs.length) % this.tabs.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = this.tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.open(this.items[next]);
      this.render();
      if (this.tabs[next]) this.tabs[next].focus();
    }

    /* Accordions toggle independently and several may be open at once - that
       is the pattern's own affordance, and on a phone comparing description
       against specs without collapsing one is worth having. */
    onHeaderClick(event) {
      var header = event.target.closest('[data-accordion]');
      if (!header) return;
      var index = this.headers.indexOf(header);
      if (index === -1) return;
      var item = this.items[index];
      item.__expanded = !item.__expanded;
      if (item.__expanded) this.selectedItem = item;
      this.render();
    }

    onModeChange() {
      var self = this;
      if (this.isDesktop) {
        // Several accordions may have been open; the tab view can only show
        // one, so keep the first open panel rather than resetting.
        var firstOpen = this.items.filter(function (item) {
          return item.__expanded;
        })[0];
        if (firstOpen) this.selectedItem = firstOpen;
      } else {
        this.items.forEach(function (item) {
          item.__expanded = item === self.selectedItem;
        });
      }
      this.render();
    }

    onHashChange() {
      var item = this.itemFromHash();
      if (!item) return;
      this.open(item);
      this.render();
      this.scrollTo(item);
    }

    /* Two ways to hit a panel by hash, because two kinds of link exist:
       `#reviews` / `#specifications` name the tab itself, while an app widget
       links to an id of its own somewhere inside a panel. Resolving the second
       by containment means review permalinks keep working without this file
       knowing anything about the app. */
    itemFromHash() {
      var hash = window.location.hash.slice(1);
      if (!hash) return null;

      var byKey = this.items.filter(function (item) {
        return item.getAttribute('data-tab-key') === hash;
      })[0];
      if (byKey) return byKey;

      var target;
      try {
        target = document.getElementById(hash);
      } catch (error) {
        return null;
      }
      if (!target) return null;

      var index = this.panels.findIndex(function (panel) {
        return panel && (panel === target || panel.contains(target));
      });
      return index === -1 ? null : this.items[index];
    }

    open(item) {
      if (!item) return;
      this.selectedItem = item;
      item.__expanded = true;
    }

    scrollTo(item) {
      if (!item) return;
      // After the panel is visible, not before - the browser's own jump
      // happened while it was still hidden.
      requestAnimationFrame(function () {
        item.scrollIntoView({ block: 'start', behavior: 'auto' });
      });
    }

    /* ---------------------------------------------------------------- */
    /* Recommendations                                                   */
    /* ---------------------------------------------------------------- */
    /* Sections cannot nest, so the "You may also like" grid cannot be printed
       inside a tab panel: it belongs to sections/related-products.liquid,
       which is the only thing that can obtain it (Shopify does not populate
       `recommendations` on a normal page load, so that section fetches itself
       back through the Section Rendering API and swaps in the result).
       Reimplementing that fetch here would mean building card markup in JS -
       money outside the `money` filter, and a second copy of
       snippets/product-card.liquid to drift.

       So the fetch is left exactly where it is and only its OUTPUT moves: this
       watches the element for the swap, relocates the rendered node into the
       panel, and hides the now-empty standalone section. Watching the DOM
       rather than agreeing an event with related-products.js keeps the
       dependency one-way - that file does not know this one exists, and
       nothing breaks if the section is removed from the template.

       The destination is the empty [data-recs-slot] the recommendations block
       printed, NOT the panel: that block is one of several a merchant may
       have put in the tab, so the grid lands in its own place and the text
       and images around it survive. */
    watchRecommendations() {
      if (!this.recsSlot) return;

      var host = document.querySelector('product-recommendations');
      if (!host) return;

      // Already populated: the fetch can resolve before this element upgrades,
      // and in the theme editor the main section is re-rendered on its own
      // while the recommendations stay put.
      if (this.adoptRecommendations(host)) return;

      /* Nothing to adopt AND the host is empty: its one fetch already ran and
         its output was adopted by a previous incarnation of this section that
         the theme editor has since re-rendered away. Ask it to fetch again -
         the observer below adopts the result when it lands. Harmless on a
         normal page load, where the host is only empty because the fetch is
         still in flight and refetch() is a guarded no-op. */
      if (host.innerHTML.trim() === '' && typeof host.refetch === 'function') {
        host.refetch();
      }

      var self = this;
      this.recsObserver = new MutationObserver(function () {
        self.adoptRecommendations(host);
      });
      this.recsObserver.observe(host, { childList: true, subtree: true });
    }

    adoptRecommendations(host) {
      var content = host.firstElementChild;
      if (!content) return false;

      var slot = this.recsSlot;
      if (!slot) return false;

      slot.innerHTML = '';
      slot.appendChild(content);

      // Emptied, not deleted: the element keeps its data-url and its custom
      // element definition, so a re-fetch (theme editor, or a repeat swap)
      // still lands somewhere real and is picked up by the observer again.
      host.setAttribute('data-relocated', '');

      this.recsSlot = null;
      if (this.recsObserver) this.recsObserver.disconnect();

      // A tab whose ONLY content was this grid was printed hidden, because
      // until now it had nothing to show. The slot may equally sit in a tab
      // that was always visible, or above the tabs entirely, in which case
      // there is nothing to reveal.
      var item = slot.closest('[data-tab-item]');
      if (item && item.hasAttribute('hidden')) {
        item.removeAttribute('hidden');
        item.__expanded = false;
      }

      this.buildTabs();
      this.collect();
      // Nothing was selected while this was the only tab; and a URL that
      // pointed here deserves to land here now that there is something to
      // land on. Neither case steals selection from a visible tab.
      if (!this.selectedItem) this.selectedItem = this.items[0] || null;
      if (item && this.itemFromHash() === item) this.open(item);
      this.render();
      return true;
    }

    render() {
      var desktop = this.isDesktop;
      var self = this;

      this.tabs.forEach(function (tab, i) {
        var isSelected = self.items[i] === self.selectedItem;
        tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        tab.setAttribute('tabindex', isSelected ? '0' : '-1');
      });

      this.items.forEach(function (item, i) {
        var panel = self.panels[i];
        var header = self.headers[i];
        var tab = self.tabs[i];
        if (!panel) return;

        var isOpen = desktop ? item === self.selectedItem : !!item.__expanded;

        /* Was this panel invisible immediately before this render?

           Read BEFORE data-inactive is cleared, and counting it as hidden,
           because the server hides the panels that way and `hidden` only takes
           over once we get here. Computing this from `hidden` alone - which is
           what it used to do - made the FIRST render of the initially-open tab
           look like a panel that had always been visible, so the two wake-ups
           below never fired for it. That is the one panel guaranteed to be
           holding content laid out at zero height, and it is the panel every
           visitor sees first. */
        var wasHidden = panel.hasAttribute('hidden') || panel.hasAttribute('data-inactive');

        // The server's pre-hide hint has done its job once we control `hidden`.
        panel.removeAttribute('data-inactive');

        if (desktop) {
          panel.setAttribute('role', 'tabpanel');
          if (tab && tab.id) panel.setAttribute('aria-labelledby', tab.id);
          // Recommended when a tab panel holds no focusable content: without
          // it, Tab out of the tablist skips straight past the panel's text.
          panel.setAttribute('tabindex', '0');
        } else {
          panel.setAttribute('role', 'region');
          if (header && header.id) panel.setAttribute('aria-labelledby', header.id);
          panel.removeAttribute('tabindex');
        }

        if (header) header.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        if (isOpen) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }

        // Widgets injected by an app (reviews) measure themselves on load and
        // come out at zero height if they were laid out while hidden. Nudging
        // them when their panel first appears is cheaper than trying to detect
        // which app is present.
        if (isOpen && wasHidden) {
          window.dispatchEvent(new Event('resize'));

          /* And tell reveal.js explicitly, rather than relying on that resize
             to reach it by accident.
             
             Everything in a [hidden] panel has no rect, so
             IntersectionObserver never reports it and reveal.js's geometric
             sweep skips it for having no height. reveal.js also MASKS every
             h2/h3 - the heading is clipped and an inner span does the moving -
             so a heading that is never revealed is not merely un-animated, it
             is a blank space where a title should be. That is exactly what
             happened to the overview: four headings inside the first tab,
             none of them ever shown.
             
             The resize above did reach reveal.js and would sometimes cover
             this, which is worse than not covering it - it worked often
             enough to look correct and failed on the first paint, where it
             matters most. Said outright now, and the resize goes back to
             meaning only what its own comment says. */
          if (window.ThemeReveal) window.ThemeReveal.init(panel);
        }
      });
    }
  }

  customElements.define('product-tabs', ProductTabs);
})();

/* Arriving from "Write a review" on the home or reviews page
   (sections/reviews-showcase.liquid) with ?write_review=1#reviews: the hash
   has already opened the Ratings & reviews tab above; this waits for
   Judge.me's widget to draw its "Write a review" link and presses it, so the
   form is open and in view without the customer hunting for it. Gives up
   quietly after ~12s if the widget never appears. */
(function () {
  var params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (e) {
    return;
  }
  if (!params.has('write_review')) return;
  /* Judge.me ships its "Write a review" link hidden and only reveals it once
     its form script has loaded - which it starts when a scroll brings the
     widget into view. Arriving here the tab is opened by script with no
     scroll, so the link stayed hidden and this used to wait for it until it
     gave up: the customer landed on the product with no form. A click on
     the hidden link is Judge.me's other trigger for loading the form (a
     delegated handler on the document), so press it to start the load, then
     press it again once the form script has revealed it. */
  var tries = 0;
  var timer = window.setInterval(function () {
    tries++;
    var link = document.querySelector('.jdgm-write-rev-link');
    if (link && link.offsetParent !== null) {
      window.clearInterval(timer);
      link.click();
      window.setTimeout(function () {
        var form = document.querySelector('.jdgm-form-wrapper, .jdgm-form, .jdgm-write-review-modal') || link;
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    } else if (link && window.jdgm && tries % 5 === 0) {
      /* Every second until the form script has loaded, because window.jdgm
         can exist before Judge.me's listeners do and a single early press
         was lost. Both of Judge.me's triggers: a scroll event (its loader
         checks on scroll whether the widget is in view) and a press of the
         link - never followed as a real link, since its href is "#" and
         resetting the hash closed the reviews tab. */
      window.dispatchEvent(new Event('scroll'));
      link.addEventListener('click', function (event) { event.preventDefault(); }, { once: true });
      link.click();
    } else if (tries > 60) {
      window.clearInterval(timer);
    }
  }, 200);
})();
